import { NodeSelection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { CONNECTOR_SOURCE_CHIP_NODE_NAME } from './connector-source-chip-node';

type DispatchableEditorView = Pick<EditorView, 'state' | 'dispatch'>;

/**
 * Handles ArrowRight/ArrowLeft keyboard events around connector source chip nodes,
 * preventing ProseMirror from creating unwanted NodeSelections on atom nodes.
 *
 * This is intentionally not exported from the package's public entry point
 * (./controllers/editor/connector-source-input-rule). It is used internally by the
 * plugin and exported only for unit-testing purposes via this internal module.
 */
export const navigateAroundConnectorSourceChip = (
	view: DispatchableEditorView,
	event: KeyboardEvent,
): boolean => {
	if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
		return false;
	}

	const { state } = view;
	const { selection } = state;
	const dir = event.key === 'ArrowRight' ? 1 : -1;

	// Case 1: Chip is currently NodeSelected (ProseMirror put it into NodeSelection).
	// Move past it to a TextSelection on the far side.
	if (selection instanceof NodeSelection) {
		if (selection.node?.type?.name === CONNECTOR_SOURCE_CHIP_NODE_NAME) {
			const targetPos = dir > 0 ? selection.$to.pos : selection.$from.pos;
			event.preventDefault();
			view.dispatch(state.tr.setSelection(TextSelection.create(state.doc, targetPos)));
			return true;
		}
		return false;
	}

	// Case 2: Collapsed TextSelection adjacent to a chip.
	// Jump to the other side to prevent ProseMirror from creating a NodeSelection.
	if (!selection.empty) {
		return false;
	}

	const { $from } = selection;
	const from = selection.from;

	if (dir > 0) {
		const nodeAfter = $from.nodeAfter;
		if (nodeAfter?.type?.name === CONNECTOR_SOURCE_CHIP_NODE_NAME) {
			event.preventDefault();
			view.dispatch(
				state.tr.setSelection(TextSelection.create(state.doc, from + nodeAfter.nodeSize)),
			);
			return true;
		}
		// Cursor is directly after a chip at the end of its parent (no nodeAfter).
		// Prevent the phantom step into the paragraph-close boundary position.
		const nodeBefore = $from.nodeBefore;
		if (
			nodeBefore?.type?.name === CONNECTOR_SOURCE_CHIP_NODE_NAME &&
			$from.parentOffset === $from.parent.content.size
		) {
			event.preventDefault();
			return true;
		}
	} else {
		const nodeBefore = $from.nodeBefore;
		if (nodeBefore?.type?.name === CONNECTOR_SOURCE_CHIP_NODE_NAME) {
			event.preventDefault();
			view.dispatch(
				state.tr.setSelection(TextSelection.create(state.doc, from - nodeBefore.nodeSize)),
			);
			return true;
		}
	}

	return false;
};
